"use client";

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import Image from "next/image";
import { Target, Clock } from "lucide-react";
import { useLandingLang } from "@/lib/landingI18n";

type CSSVars = CSSProperties & Record<`--${string}`, string | number>;

export interface EmployeeToolIcon {
  icon: ReactNode;
  label: string;
}

export interface EmployeeStat {
  label: string;
  value: string;
}

export interface EmployeeCardData {
  name: string;
  role: string;
  avatarSrc: string;
  roleIcon: ReactNode;
  goal: string;
  goals: string[];
  tasks: string[];
  skills: string[];
  allSkills: string[];
  tools: EmployeeToolIcon[];
  allTools: EmployeeToolIcon[];
  stats: EmployeeStat[];
  schedule: string;
  progress: number;
}

const RING_RADIUS = 22;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export default function EmployeeCard({
  name,
  role,
  avatarSrc,
  roleIcon,
  goal,
  goals,
  tasks,
  skills,
  allSkills,
  tools,
  allTools,
  stats,
  schedule,
  progress,
}: EmployeeCardData) {
  const { L } = useLandingLang();
  const cardRef = useRef<HTMLDivElement>(null);
  const [taskIndex, setTaskIndex] = useState(0);
  const [touchOpen, setTouchOpen] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setTaskIndex((i) => (i + 1) % tasks.length);
    }, 3800);
    return () => clearInterval(id);
  }, [tasks.length]);

  useEffect(() => {
    if (!touchOpen) return;

    const closeOnOutsideTap = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && cardRef.current?.contains(target)) return;
      setTouchOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTouchOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsideTap);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideTap);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [touchOpen]);

  const ringOffset = RING_CIRCUMFERENCE * (1 - progress / 100);
  const overlayVisibilityClass = touchOpen ? "visible opacity-100" : "invisible opacity-0";
  const overlayPanelClass = touchOpen ? "translate-y-0" : "";
  const overlayItemClass = touchOpen ? "translate-y-0 opacity-100" : "";

  return (
    <div
      ref={cardRef}
      data-employee-card
      role="button"
      aria-expanded={touchOpen}
      className="af-lift group relative overflow-hidden rounded-3xl border border-[var(--af-border)] bg-[var(--af-bg)] focus:outline-none"
      style={{ boxShadow: "var(--af-shadow)" }}
      tabIndex={0}
      onClick={() => {
        const isTouchLayout = window.matchMedia("(hover: none), (pointer: coarse), (max-width: 767px)").matches;
        if (isTouchLayout) setTouchOpen((open) => !open);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        setTouchOpen((open) => !open);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setTouchOpen(false);
      }}
    >
      {/* Portrait */}
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-[var(--af-bg-soft)]">
        <Image
          src={avatarSrc}
          alt={name}
          fill
          sizes="(min-width: 640px) 33vw, 100vw"
          className="af-breathe object-cover"
          priority
        />
        <div className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--af-bg)]/90 text-[var(--af-accent)] shadow-sm backdrop-blur">
          {roleIcon}
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="break-words text-[15px] font-semibold leading-snug text-[var(--af-text)]">{name}</h3>
            <p className="mt-0.5 text-[13px] text-[var(--af-muted)]">{role}</p>
          </div>
          <div className="relative h-11 w-11 shrink-0" role="img" aria-label={L.card.performanceAria(progress)}>
            <svg viewBox="0 0 56 56" className="h-11 w-11 -rotate-90" aria-hidden="true">
              <circle cx="28" cy="28" r={RING_RADIUS} fill="none" stroke="var(--af-border)" strokeWidth="4" />
              <circle
                cx="28"
                cy="28"
                r={RING_RADIUS}
                fill="none"
                stroke="var(--af-accent)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                className="af-ring-progress"
                style={{
                  "--af-ring-from": RING_CIRCUMFERENCE,
                  "--af-ring-to": ringOffset,
                  strokeDashoffset: ringOffset,
                } as CSSVars}
              />
            </svg>
            <span
              aria-hidden="true"
              className="af-nums absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-[var(--af-text)]"
            >
              {progress}%
            </span>
          </div>
        </div>

        <div className="mt-2.5 flex items-center gap-1.5 text-[12px] text-[var(--af-muted)]">
          <span className="af-pulse-dot h-1.5 w-1.5 rounded-full bg-[#4ade80]" />
          {L.card.workingNow}
        </div>

        <div className="mt-3 flex items-start gap-1.5 text-[13px] text-[var(--af-text)]">
          <Target size={14} className="mt-0.5 shrink-0 text-[var(--af-accent)]" />
          <span className="font-medium">{goal}</span>
        </div>

        <div className="mt-1.5 h-5 overflow-hidden">
          <p key={taskIndex} className="af-task-line truncate text-[12.5px] text-[var(--af-muted)]">
            {tasks[taskIndex]}
          </p>
        </div>

        <div className="mt-3.5 flex flex-wrap gap-1.5">
          {skills.map((skill, i) => (
            <span
              key={skill}
              className="af-fade-in rounded-full border border-[var(--af-border)] bg-[var(--af-bg-soft)] px-2.5 py-1 text-[11px] font-medium text-[var(--af-muted)]"
              style={{ animationDelay: `${i * 0.12}s` }}
            >
              {skill}
            </span>
          ))}
        </div>

        <div className="mt-3.5 flex items-center gap-2 border-t border-[var(--af-border)] pt-3.5">
          <div className="flex -space-x-1.5">
            {tools.map((tool) => (
              <span
                key={tool.label}
                title={tool.label}
                className="flex h-6 w-6 items-center justify-center rounded-full border border-[var(--af-border)] bg-[var(--af-bg)] text-[11px] text-[var(--af-muted)]"
              >
                {tool.icon}
              </span>
            ))}
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-1 text-[11.5px] text-[var(--af-muted)]">
            <Clock size={12} className="shrink-0" />
            <span className="truncate">{schedule}</span>
          </div>
        </div>
      </div>

      {/* Hover / keyboard-focus detail overlay — the full picture: all skills, tools, goals, performance */}
      <div
        data-employee-overlay
        className={`${overlayVisibilityClass} absolute inset-0 z-10 flex flex-col overflow-y-auto rounded-3xl bg-[var(--af-glass-strong)] p-4 backdrop-blur-md transition-all duration-300 ease-out group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100`}
        aria-hidden={!touchOpen}
      >
        <div className={`translate-y-2 transition-transform duration-300 ease-out group-hover:translate-y-0 group-focus-visible:translate-y-0 ${overlayPanelClass}`}>
          <h3 className="text-[15px] font-semibold text-[var(--af-text)]">{name}</h3>
          <p className="mt-0.5 text-[12px] text-[var(--af-muted)]">{role}</p>

          <div className="mt-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[var(--af-muted)]">
              {L.card.goalsHeading}
            </div>
            <ul className="mt-1.5 space-y-1">
              {goals.map((g, i) => (
                <li
                  key={g}
                  className={`translate-y-1 text-[12.5px] text-[var(--af-text)] opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100 ${overlayItemClass}`}
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  {g}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[var(--af-muted)]">
              {L.card.allSkillsHeading}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {allSkills.map((skill, i) => (
                <span
                  key={skill}
                  className={`translate-y-1 rounded-full border border-[var(--af-border)] bg-[var(--af-bg)] px-2.5 py-1 text-[11px] font-medium text-[var(--af-muted)] opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100 ${overlayItemClass}`}
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[var(--af-muted)]">
              {L.card.toolsHeading}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {allTools.map((tool, i) => (
                <span
                  key={tool.label}
                  className={`flex translate-y-1 items-center gap-1.5 rounded-full border border-[var(--af-border)] bg-[var(--af-bg)] px-2.5 py-1 text-[11px] text-[var(--af-muted)] opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100 ${overlayItemClass}`}
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  {tool.icon}
                  {tool.label}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[var(--af-muted)]">
              {L.card.performanceHeading}
            </div>
            <div className="mt-1.5 grid grid-cols-3 gap-1.5">
              {stats.map((stat, i) => (
                <div
                  key={stat.label}
                  className={`translate-y-1 rounded-xl bg-[var(--af-bg)] px-1.5 py-2 text-center opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100 ${overlayItemClass}`}
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  <div className="af-nums text-[13px] font-semibold text-[var(--af-accent)]">{stat.value}</div>
                  <div className="mt-0.5 text-[9.5px] leading-tight text-[var(--af-muted)]">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
